import { X } from 'lucide-react'
import DOMPurify from 'dompurify'
import { Template } from '@/types'

interface TemplatePreviewProps {
  template: Template
  onClose: () => void
}

export function TemplatePreview({ template, onClose }: TemplatePreviewProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="relative h-[90vh] w-full max-w-5xl overflow-hidden rounded-2xl bg-slate-900 border border-slate-700 shadow-2xl">
        {/* Header - Fixed */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-700 bg-slate-900 px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-white">
              {template.name}
            </h2>
            <p className="text-sm text-slate-400 mt-1">Template Preview</p>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-10 h-10 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors border border-slate-600"
            title="Close preview"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 h-[calc(90vh-80px)]">
          {/* Subject Line */}
          <div className="mb-6">
            <h3 className="mb-2 text-sm font-medium text-slate-400">Subject Line</h3>
            <div className="rounded-xl border border-slate-700 bg-slate-800 p-4">
              <p className="text-base font-medium text-white">{template.subject}</p>
            </div>
          </div>

          {/* Variables Info */}
          {template.variables && template.variables.length > 0 && (
            <div className="mb-6">
              <h3 className="mb-2 text-sm font-medium text-slate-400">Template Variables</h3>
              <div className="flex flex-wrap gap-2">
                {template.variables.map((variable) => (
                  <span
                    key={variable}
                    className="inline-flex rounded-full bg-cyan-500/15 px-3 py-1 text-sm font-medium text-cyan-400 border border-cyan-500/20"
                  >
                    {`{{${variable}}}`}
                  </span>
                ))}
              </div>
              <p className="mt-2 text-xs text-slate-500">
                Variables will be replaced with actual client data when sending emails
              </p>
            </div>
          )}

          {/* HTML Content */}
          <div>
            <h3 className="mb-2 text-sm font-medium text-slate-400">Email Body</h3>
            <div className="rounded-xl border border-slate-700 bg-white overflow-hidden">
              <div
                className="p-6 min-h-[300px]"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(template.bodyHtml || '<p style="color: #666;">No content</p>') }}
              />
            </div>
          </div>

          {/* Plain Text Version */}
          {template.bodyText && (
            <div className="mt-6">
              <h3 className="mb-2 text-sm font-medium text-slate-400">Plain Text Version</h3>
              <div className="rounded-xl border border-slate-700 bg-slate-800 p-4 max-h-[200px] overflow-auto">
                <pre className="whitespace-pre-wrap font-mono text-sm text-slate-300">
                  {template.bodyText}
                </pre>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 border-t border-slate-700 bg-slate-900 px-6 py-4">
          <button
            onClick={onClose}
            className="w-full rounded-xl bg-slate-800 hover:bg-slate-700 px-6 py-3 text-sm font-medium text-white transition-colors border border-slate-600"
          >
            Close Preview
          </button>
        </div>
      </div>
    </div>
  )
}
